import { CommandProps, Mark, mergeAttributes } from '@tiptap/core';
import type { Mark as PMark, MarkType, Node } from '@tiptap/pm/model';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

export interface MentionOptions {
  currentUserId: number | null;
  currentUserType: string | null;
}

export interface MentionAttrs {
  id: number | string;
  label: string;
  url?: string;
  instanceId?: string;
}

interface MentionRange {
  from: number;
  to: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mention: {
      insertMention: (attrs: MentionAttrs) => ReturnType;
    };
  }
}

/**
 * 部分的に選択されたメンションを検出
 */
const findPartiallySelectedMentions = (
  state: EditorState,
  from: number,
  to: number,
): MentionRange[] => {
  if (from === to) {
    return [];
  }

  const doc = state.doc;
  const mentionsToRemove: MentionRange[] = [];

  doc.nodesBetween(from, to, (node: Node, pos: number) => {
    if (!node.isText || !node.marks) {
      return;
    }

    node.marks.forEach((mark: PMark) => {
      if (mark.type.name !== 'mention') {
        return;
      }

      const markStart = pos;
      const markEnd = pos + node.nodeSize;

      // メンションが部分的に選択されている場合
      const isPartiallySelected =
        (from > markStart && from < markEnd) ||
        (to > markStart && to < markEnd) ||
        (from <= markStart && to >= markEnd);

      if (isPartiallySelected) {
        mentionsToRemove.push({ from: markStart, to: markEnd });
      }
    });
  });

  return mentionsToRemove;
};

const removeMentionMarks = (
  tr: Transaction,
  mentionRanges: MentionRange[],
  mentionMarkType: MarkType,
): void => {
  mentionRanges.forEach(({ from, to }) => {
    tr.removeMark(from, to, mentionMarkType);
  });
};

/**
 * クリップボードに非同期でテキストを保存
 * モダンブラウザのClipboard APIを使用し、フォールバックとしてexecCommandを提供
 */
const saveToClipboard = async (text: string): Promise<void> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error('Clipboard API not available');
    }
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};

/**
 * 切り取り操作の共通処理
 * メンションマークの削除、テキスト削除、カーソル位置の設定を行う
 */
const performCutOperation = (
  state: EditorState,
  tr: Transaction,
  from: number,
  to: number,
  mentionMarkType: MarkType | null,
): { selectedText: string; hasMentions: boolean } => {
  if (from === to) {
    return { selectedText: '', hasMentions: false };
  }

  const mentionRanges = findPartiallySelectedMentions(state, from, to);
  const hasMentions = mentionRanges.length > 0;

  const selectedText = state.doc.textBetween(from, to);

  if (hasMentions && mentionMarkType) {
    removeMentionMarks(tr, mentionRanges, mentionMarkType);
  }

  tr.delete(from, to);
  const newPos = Math.min(from, tr.doc.content.size);
  tr.setSelection(TextSelection.create(tr.doc, newPos));

  return { selectedText, hasMentions };
};

/**
 * メンション切り取り処理のメイン関数
 * メンションが含まれている場合のみ独自処理を行い、それ以外はデフォルト動作を継続
 */
const handleCutWithMentionCheck = (
  tr: Transaction,
  state: EditorState,
  view: EditorView,
  mentionMarkType: MarkType | null,
): boolean => {
  const { selection } = state;
  const { from, to } = selection;

  if (from === to) {
    return false;
  }

  const mentionRanges = findPartiallySelectedMentions(state, from, to);
  const hasMentions = mentionRanges.length > 0;

  if (!hasMentions) {
    return false;
  }

  const result = performCutOperation(state, tr, from, to, mentionMarkType);

  if (result.selectedText.length > 0) {
    // 非同期でクリップボードに保存（エディタの処理を阻害しない）
    setTimeout(() => {
      saveToClipboard(result.selectedText).then(() => {
        // 保存完了後、エディタにフォーカスを戻す
        if (view && view.dom) {
          view.focus();
        }
      });
    }, 0);

    return true;
  }

  return false;
};

export const MentionMark = Mark.create<MentionOptions>({
  name: 'mention',
  priority: 1000,
  excludes: 'link',
  inclusive: false,
  exitable: true,
  isolating: true,
  addOptions() {
    return { currentUserId: null, currentUserType: null };
  },
  addAttributes() {
    return {
      id: { default: null },
      label: { default: null },
      url: { default: null },
      instanceId: { default: null },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'a.mention[data-mention-id]',
        getAttrs: (node) => {
          const element = node as HTMLElement;
          return {
            id: element.getAttribute('data-mention-id'),
            label: (element.textContent ?? '').replace(/^@/, ''),
            url: element.getAttribute('href') ?? '',
            instanceId:
              element.getAttribute('data-instance-id') ?? `mention-${Date.now()}-${Math.random()}`,
          };
        },
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const { id, url, instanceId } = HTMLAttributes;
    const numericId = Number(id);
    let mentionedUserType = null;
    if (url && url.includes('/company/')) {
      mentionedUserType = 'COMPANY_USER';
    } else if (url && url.includes('/tenant/')) {
      mentionedUserType = 'TENANT_USER';
    }
    const isYellow =
      numericId === -1 ||
      (this.options.currentUserType === mentionedUserType &&
        this.options.currentUserId !== null &&
        this.options.currentUserId === numericId);

    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        class: 'mention',
        'data-mention-id': id,
        'data-mention-type': isYellow ? 'yellow' : 'blue',
        'data-instance-id': instanceId || `mention-${Date.now()}-${Math.random()}`,
        href: url || '',
      }),
      0,
    ];
  },
  addKeyboardShortcuts() {
    return {
      'Mod-x': () =>
        this.editor.commands.command(({ tr, state, view }) => {
          return handleCutWithMentionCheck(tr, state, view, this.type);
        }),
    };
  },
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('mentionCutHandler'),
        props: {
          handleDOMEvents: {
            cut: (view) => {
              const { state } = view;
              const tr = state.tr;
              const mentionMarkType = state.schema.marks.mention;

              const result = handleCutWithMentionCheck(tr, state, view, mentionMarkType);

              if (result) {
                view.dispatch(tr);
              }

              return result;
            },
          },
        },
      }),
    ];
  },
  addCommands() {
    return {
      insertMention:
        (attrs: MentionAttrs) =>
        ({ commands }: CommandProps) => {
          const attrsWithInstanceId = {
            ...attrs,
            instanceId: attrs.instanceId || `mention-${Date.now()}-${Math.random()}`,
          };
          return commands.insertContent([
            {
              type: 'text',
              text: `@${attrs.label}`,
              marks: [
                {
                  type: this.name,
                  attrs: attrsWithInstanceId,
                },
              ],
            },
            { type: 'text', text: ' ' },
          ]);
        },
    };
  },
});
